import React, { useState } from "react";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { v4 as uuidv4 } from "uuid";
import { DropZone } from "./DropZone";
import { colors } from "../../global";
import TableEditor from "./TableEditor";
import { AddSlide } from "./AddSlide";

function formatLabel(str) {
  return str
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

const DraggableChart = ({ name }) => {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: "CHART",
    item: { name },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
  }));

  return (
    <div
      ref={drag}
      className="cursor-move p-3 rounded-lg border transition-all duration-200 hover:shadow-sm"
      style={{
        opacity: isDragging ? 0.5 : 1,
        backgroundColor: colors.white,
        borderColor: colors.gray,
        color: colors.primary,
      }}
    >
      <div className="flex items-center gap-2">
        <div
          className="w-2 h-2 rounded-full"
          style={{ backgroundColor: colors.accent }}
        ></div>
        <span className="text-sm font-medium">{name}</span>
      </div>
    </div>
  );
};

const SlideTemplate = ({
  slide,
  index,
  onDropChart,
  layout,
  onRemoveChart,
}) => {
  const renderZoneContent = (zoneKey) => {
    const content = slide[zoneKey];
    if (!content) return null;

    if (typeof content === "string") {
      return (
        <div
          className="flex justify-between items-center w-full h-full rounded-lg px-3 py-2"
          style={{ backgroundColor: `${colors.primary}10` }}
        >
          <span
            className="text-sm font-medium text-center truncate"
            style={{ color: colors.primary }}
          >
            {content}
          </span>

          <button
            onClick={(e) => {
              e.stopPropagation();
              onRemoveChart(index, zoneKey);
            }}
            className="ml-2 w-6 h-6 rounded-full flex items-center justify-center hover:opacity-80 transition"
            style={{
              backgroundColor: colors.error,
              color: colors.white,
            }}
            aria-label="Remove chart"
          >
            ×
          </button>
        </div>
      );
    } else if (content.type === "table") {
      return (
        <div className="w-full">
          <TableEditor
            tableData={content.table_data}
            setTableData={(newTable) => {
              onDropChart(index, zoneKey, { ...content, table_data: newTable });
            }}
          />
          <button
            className="mt-2 px-3 py-1 rounded text-sm font-medium transition-colors"
            style={{
              backgroundColor: colors.error,
              color: colors.white,
            }}
            onClick={(e) => {
              e.stopPropagation();
              onRemoveChart(index, zoneKey);
            }}
          >
            Remove Table
          </button>
        </div>
      );
    }
    return null;
  };

  if (layout === 5) {
    return (
      <div className="grid grid-cols-2 grid-rows-2 gap-3 p-4">
        <DropZone
          onDrop={(chartName) => onDropChart(index, "zone1", chartName)}
        >
          {renderZoneContent("zone1")}
        </DropZone>
        <DropZone
          onDrop={(chartName) => onDropChart(index, "zone2", chartName)}
        >
          {renderZoneContent("zone2")}
        </DropZone>
        <div className="col-span-2">
          <DropZone
            onDrop={(chartName) => onDropChart(index, "zone3", chartName)}
          >
            {renderZoneContent("zone3")}
          </DropZone>
        </div>
      </div>
    );
  }

  const zones = [];
  if (layout === 1) zones.push("zone1");
  if (layout === 2 || layout === 3) zones.push("zone1", "zone2");
  if (layout === 4) zones.push("zone1", "zone2", "zone3", "zone4");

  return (
    <div className={`grid gap-3 ${getGridClass(layout)} p-4`}>
      {zones.map((zoneKey) => (
        <DropZone
          key={zoneKey}
          onDrop={(chartName) => onDropChart(index, zoneKey, chartName)}
        >
          {renderZoneContent(zoneKey)}
        </DropZone>
      ))}
    </div>
  );
};

const getGridClass = (layout) => {
  switch (layout) {
    case 1:
      return "grid-cols-1";
    case 2:
      return "grid-cols-2";
    case 3:
      return "grid-rows-2";
    case 4:
      return "grid-cols-2 grid-rows-2";
    case 5:
      return "grid-cols-2 grid-rows-2";
    default:
      return "grid-cols-1";
  }
};

const SlideBuilder = ({ onExport, contents }) => {
  const [slides, setSlides] = useState([]);
  if (!contents.includes("Custom Table")) contents.push("Custom Table");

  const ItemTypes = { SLIDE: "slide" };

  const DraggableSlide = ({ slide, index, moveSlide, children }) => {
    const ref = React.useRef(null);

    const [, drop] = useDrop({
      accept: ItemTypes.SLIDE,
      hover(item, monitor) {
        if (!ref.current) return;

        const dragIndex = item.index;
        const hoverIndex = index;

        if (dragIndex === hoverIndex) return;

        const hoverBoundingRect = ref.current.getBoundingClientRect();
        const hoverMiddleY =
          (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
        const clientOffset = monitor.getClientOffset();
        const hoverClientY = clientOffset.y - hoverBoundingRect.top;

        if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
        if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;

        moveSlide(dragIndex, hoverIndex);
        item.index = hoverIndex;
      },
    });

    const [{ isDragging }, drag] = useDrag({
      type: ItemTypes.SLIDE,
      item: { type: ItemTypes.SLIDE, index },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    });

    drag(drop(ref));

    return (
      <div ref={ref} style={{ opacity: isDragging ? 0.3 : 1, cursor: "move" }}>
        {children}
      </div>
    );
  };

  const moveSlide = (fromIndex, toIndex) => {
    setSlides((prevSlides) => {
      const updated = [...prevSlides];
      const [moved] = updated.splice(fromIndex, 1);
      updated.splice(toIndex, 0, moved);
      return updated;
    });
  };

  const addSlide = (layout, title) => {
    const newSlide = {
      id: uuidv4(),
      layout,
      title: title,
      zone1: null,
      zone2: null,
      zone3: null,
      zone4: null,
      information: [],
    };
    setSlides((prev) => [...prev, newSlide]);
  };

  const handleDropChart = (slideIndex, zoneKey, chartNameOrObj) => {
    setSlides((prevSlides) => {
      const updated = [...prevSlides];

      if (typeof chartNameOrObj === "string") {
        if (chartNameOrObj === "Custom Table") {
          updated[slideIndex][zoneKey] = {
            type: "table",
            table_data: [
              ["Header 1", "Header 2"],
              ["Row 1 Col 1", "Row 1 Col 2"],
            ],
          };
        } else {
          updated[slideIndex][zoneKey] = chartNameOrObj;
        }
      } else if (chartNameOrObj.type === "table") {
        updated[slideIndex][zoneKey] = chartNameOrObj;
      }

      return updated;
    });
  };

  const handleRemoveChart = (slideIndex, zoneKey) => {
    setSlides((prev) => {
      const updated = [...prev];
      if (updated[slideIndex]) {
        updated[slideIndex][zoneKey] = null;
      }
      return updated;
    });
  };

  const handleDeleteSlide = (indexToRemove) => {
    setSlides((prev) => prev.filter((_, idx) => idx !== indexToRemove));
  };

  const handleExport = () => {
    const result = slides.map((slide, idx) => {
      const info = [];
      ["zone1", "zone2", "zone3", "zone4"].forEach((zone) => {
        const val = slide[zone];
        if (val) {
          if (typeof val === "string") {
            info.push({
              type: "image",
              image_path: `${val.toLowerCase().replace(/\s+/g, "_")}.png`,
            });
          } else if (val.type === "table") {
            info.push({
              type: "table",
              table_data: val.table_data,
            });
          }
        }
      });

      return {
        layout: slide.layout,
        header: slide.title,
        information: info,
      };
    });

    if (onExport) onExport(result);
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="min-h-screen p-6">
        <div className="mb-8">
          <h1
            className="text-2xl font-bold mb-2"
            style={{ color: colors.primary }}
          >
            Slide Builder
          </h1>
          <p className="text-sm" style={{ color: colors.darkGray }}>
            Drag and drop charts to create presentation slides
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-1">
            <div
              className="rounded-lg p-6 sticky top-16"
              style={{ backgroundColor: colors.white }}
            >
              <h2
                className="text-lg font-semibold mb-4"
                style={{ color: colors.primary }}
              >
                Chart Library
              </h2>
              <div className="space-y-3">
                {contents.map((name) => (
                  <DraggableChart key={name} name={formatLabel(name)} />
                ))}
              </div>
            </div>
          </div>

          <div className="lg:col-span-2">
            <div className="flex items-center justify-between mb-6">
              <h2
                className="text-lg font-semibold"
                style={{ color: colors.primary }}
              >
                Slides ({slides.length})
              </h2>
              <button
                onClick={handleExport}
                className="px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50"
                style={{
                  backgroundColor: colors.success,
                  color: colors.white,
                }}
                disabled={slides.length === 0}
              >
                Export PowerPoint
              </button>
            </div>

            <div className="space-y-6 mb-5">
              {slides.length === 0 ? (
                <AddSlide slides={slides} addSlide={addSlide} />
              ) : (
                <>
                  {slides.map((slide, index) => (
                    <DraggableSlide
                      key={slide.id}
                      slide={slide}
                      index={index}
                      moveSlide={moveSlide}
                    >
                      <div
                        className="rounded-lg border"
                        style={{
                          backgroundColor: colors.white,
                          borderColor: colors.gray,
                        }}
                      >
                        <div
                          className="flex items-center justify-between p-4 border-b"
                          style={{ borderColor: colors.gray }}
                        >
                          <p
                            className="font-semibold text-sm"
                            style={{ color: colors.primary }}
                          >
                            {slide.title}
                          </p>

                          <button
                            onClick={() => handleDeleteSlide(index)}
                            className="w-8 h-8 rounded-full flex items-center justify-center transition-colors hover:opacity-80"
                            style={{
                              backgroundColor: colors.error,
                              color: colors.white,
                            }}
                          >
                            ×
                          </button>
                        </div>

                        <SlideTemplate
                          slide={slide}
                          index={index}
                          layout={slide.layout}
                          onDropChart={handleDropChart}
                          onRemoveChart={handleRemoveChart}
                        />
                      </div>
                    </DraggableSlide>
                  ))}

                  <AddSlide slides={slides} addSlide={addSlide} />
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </DndProvider>
  );
};

export default SlideBuilder;
